import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Users, CheckSquare, TrendingUp, DollarSign, AlertTriangle, Calendar,
  Phone, Eye, RefreshCw, Filter, Settings, Award, Target, Clock,
  MessageSquare, Briefcase, ArrowUpRight, Ban, ChevronLeft, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../context/PermissionsContext';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { logger } from '../utils/logger';
import ActivityHistoryModal from '../components/ActivityHistoryModal';
import DashboardVisibilityPanel from './DashboardVisibilityPanel';
import { ChartSettingsButton, getChartSettings, getPieColors } from '../components/ChartSettings';
import { fmtKPI } from '../utils/kpiFormat';
import { useKPIDefinition } from '../components/KPIDefinitionModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt$ = fmtKPI;

const CHART_COLORS = ['#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#06b6d4', '#14b8a6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#64748b', '#84cc16'];

// ===== KPI Card Component =====
const DashKPI = ({ title, value, sub, icon: Icon, variant = 'default', onClick }) => {
  const colors = {
    default: 'bg-slate-50 text-slate-600',
    gold: 'bg-amber-50 text-amber-600',
    green: 'bg-emerald-50 text-emerald-600',
    blue: 'bg-blue-50 text-blue-600',
    red: 'bg-red-50 text-red-600',
    purple: 'bg-purple-50 text-purple-600',
  };
  return (
    <div
      className={`bg-white rounded-xl border border-slate-100 p-3 shadow-sm hover:shadow-md transition-shadow ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      data-testid={`dash-kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className={`inline-flex p-1.5 rounded-lg mb-2 ${colors[variant]}`}>
        <Icon size={14} strokeWidth={2} />
      </div>
      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider leading-tight">{title}</p>
      <p className="text-lg font-bold text-slate-900 mt-0.5">{value}</p>
      {sub && <p className="text-[10px] text-slate-500 mt-0.5 truncate">{sub}</p>}
    </div>
  );
};

// ===== Stage Distribution Pie (reusable) =====
const MiniPie = ({ data, total }) => {
  const [hov, setHov] = useState(null);
  const stages = data.filter(s => s.count > 0);
  if (!stages.length) return <p className="text-sm text-slate-400 text-center py-8">No data</p>;
  let cum = 0;
  const slices = stages.map((s, i) => {
    const angle = (s.count / total) * 360;
    const start = cum; cum += angle;
    const r = 90, mid = start + angle / 2;
    const sr = (start - 90) * Math.PI / 180, er = (cum - 90) * Math.PI / 180;
    const mr = (mid - 90) * Math.PI / 180;
    const x1 = 100 + r * Math.cos(sr), y1 = 100 + r * Math.sin(sr);
    const x2 = 100 + r * Math.cos(er), y2 = 100 + r * Math.sin(er);
    const path = stages.length === 1
      ? `M 100 100 m -${r} 0 a ${r} ${r} 0 1 1 ${r*2} 0 a ${r} ${r} 0 1 1 -${r*2} 0`
      : `M 100 100 L ${x1} ${y1} A ${r} ${r} 0 ${angle > 180 ? 1 : 0} 1 ${x2} ${y2} Z`;
    return { ...s, path, color: CHART_COLORS[i % CHART_COLORS.length], tipX: 100 + 60 * Math.cos(mr), tipY: 100 + 60 * Math.sin(mr), idx: i };
  });
  return (
    <div className="flex flex-col sm:flex-row items-start gap-4">
      <div className="flex flex-col gap-1.5 w-full sm:w-[40%] sm:flex-shrink-0 pt-2">
        {slices.map(s => (
          <div key={s.stage_id} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all cursor-default ${hov === s.idx ? 'bg-slate-100 scale-[1.02]' : ''}`}
            onMouseEnter={() => setHov(s.idx)} onMouseLeave={() => setHov(null)}>
            <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: s.color }} />
            <span className="text-sm text-slate-700 truncate flex-1">{s.label}</span>
            <span className="text-sm font-semibold text-slate-900 tabular-nums">{s.count}</span>
            <span className="text-xs text-slate-400 w-10 text-right">{s.percentage}%</span>
          </div>
        ))}
      </div>
      <div className="flex-1 relative flex items-center justify-center" style={{ minHeight: '300px' }}>
        <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%', minHeight: '280px' }}>
          {slices.map(s => (
            <path key={s.stage_id} d={s.path} fill={s.color}
              opacity={hov !== null && hov !== s.idx ? 0.35 : 1}
              className="transition-all duration-200 cursor-pointer"
              style={{ transform: hov === s.idx ? `translate(${(s.tipX - 100) * 0.04}px, ${(s.tipY - 100) * 0.04}px)` : 'none' }}
              onMouseEnter={() => setHov(s.idx)} onMouseLeave={() => setHov(null)} />
          ))}
          <circle cx="100" cy="100" r="50" fill="white" />
          <text x="100" y="95" textAnchor="middle" style={{ fontSize: '22px', fontWeight: 800 }} className="fill-slate-900">{total}</text>
          <text x="100" y="113" textAnchor="middle" style={{ fontSize: '10px' }} className="fill-slate-400">total leads</text>
        </svg>
        {hov !== null && slices[hov] && (
          <div className="absolute top-2 right-2 bg-slate-900 text-white px-3 py-1.5 rounded-lg text-xs z-10 pointer-events-none whitespace-nowrap shadow-lg">
            <span className="font-semibold">{slices[hov].label}</span> | {slices[hov].count} leads | {slices[hov].percentage}%
          </div>
        )}
      </div>
    </div>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { role, user } = usePermissions();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [repFilter, setRepFilter] = useState('all');
  const [reps, setReps] = useState([]);
  const [chartYear, setChartYear] = useState(new Date().getFullYear());
  const [commOpen, setCommOpen] = useState(false);
  const [commSettings, setCommSettings] = useState(null);
  const [commSaving, setCommSaving] = useState(false);
  const [activityModal, setActivityModal] = useState({ open: false, leadId: null });
  const [visibilityOpen, setVisibilityOpen] = useState(false);
  const [widgetVisibility, setWidgetVisibility] = useState({});
  const [chartSettings, setChartSettings] = useState(getChartSettings);
  const [incentiveMonth, setIncentiveMonth] = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
  });
  const todayISO = () => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
  };
  const [jobDate, setJobDate] = useState(todayISO);
  const [achDate, setAchDate] = useState(todayISO);
  const { openKPI, modal: kpiModal } = useKPIDefinition();

  const isAE = role === 'account_executive';
  const canManageComm = ['admin', 'owner'].includes(role);

  // Incentive month helpers
  const shiftIncentiveMonth = (delta) => {
    const [y, m] = incentiveMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setIncentiveMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };
  const isCurrentOrFutureMonth = (ym) => {
    const now = new Date();
    const cur = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    return ym >= cur;
  };

  // Per-day navigator helpers for Today's Job / Today's Achievements
  const shiftDate = (setter, current, delta) => {
    const [y, m, day] = current.split('-').map(Number);
    const d = new Date(y, m - 1, day + delta);
    setter(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
  };
  const isAtOrAfterToday = (iso) => iso >= todayISO();

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (!isAE && repFilter !== 'all') params.append('rep_id', repFilter);
      if (incentiveMonth) params.append('incentive_month', incentiveMonth);
      if (jobDate) params.append('job_date', jobDate);
      if (achDate) params.append('achievement_date', achDate);
      const response = await fetch(`${API_URL}/api/dashboard/ae-dashboard?${params}`, { credentials: 'include' });
      if (response.ok) setData(await response.json());
    } catch (err) { logger.error('Dashboard fetch error:', err); }
  }, [isAE, repFilter, incentiveMonth, jobDate, achDate]);

  const fetchReps = useCallback(async () => {
    if (isAE) return;
    try {
      const r = await fetch(`${API_URL}/api/users`, { credentials: 'include' });
      if (r.ok) {
        const users = await r.json();
        setReps(users.filter(u => u.role === 'account_executive' || u.role === 'owner' || u.role === 'admin'));
      }
    } catch { /* silent */ }
  }, [isAE]);

  const fetchVisibility = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/dashboard-visibility/my`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setWidgetVisibility(d.widgets || {});
      }
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchData(), fetchReps(), fetchVisibility()]);
      setLoading(false);
    };
    load();
  }, [fetchData, fetchReps, fetchVisibility]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  // Commission settings
  const openCommSettings = async () => {
    try {
      const r = await fetch(`${API_URL}/api/commission-settings`, { credentials: 'include' });
      if (r.ok) setCommSettings(await r.json());
    } catch { /* silent */ }
    setCommOpen(true);
  };

  const saveCommSettings = async () => {
    setCommSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/commission-settings`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(commSettings),
      });
      if (r.ok) { toast.success('Commission settings saved'); setCommOpen(false); fetchData(); }
      else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Failed to save'); }
    } catch { toast.error('Failed to save'); }
    setCommSaving(false);
  };

  if (loading) return (
    <div className="p-6 space-y-6">
      <Skeleton className="h-10 w-64" />
      <div className="grid grid-cols-3 gap-4"><Skeleton className="h-24" /><Skeleton className="h-24" /><Skeleton className="h-24" /></div>
      <div className="grid grid-cols-9 gap-3">{Array(9).fill(0).map((_, i) => <Skeleton key={`kpi-skel-${i}`} className="h-20" />)}</div>
    </div>
  );

  const d = data || {};
  const kpi = d.kpi_cards || {};
  const income = d.personal_income || {};
  const job = d.todays_job || {};
  const ach = d.todays_achievements || {};
  const stageDist = d.stage_distribution || {};
  const charts = d.charts || {};
  const stageLabels = d.stage_labels || {};
  const stageSem = d.stage_semantics || {};

  // Dynamic KPI titles (respect stage rename in Settings)
  const contactedLabel = stageLabels[stageSem.contacted_stage_id] || 'Contacted';
  const consultLabel = stageLabels[stageSem.consult_done_stage_id] || 'Consult Done';
  const wonLabel = (stageSem.won_stage_ids || []).map(id => stageLabels[id]).filter(Boolean).join(' / ') || 'Closed Won';

  const vis = (widgetId) => widgetVisibility[widgetId] !== false;

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5">{isAE ? 'Your personal performance' : 'Company overview'}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isAE && (
            <Select value={repFilter} onValueChange={setRepFilter}>
              <SelectTrigger className="w-full sm:w-48 h-9 text-xs" data-testid="dash-rep-filter">
                <Filter className="h-3.5 w-3.5 mr-1 text-slate-400" /><SelectValue placeholder="All Reps" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Reps</SelectItem>
                {reps.map(r => <SelectItem key={r.user_id} value={r.user_id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <ChartSettingsButton onSettingsChange={setChartSettings} />
          {canManageComm && (
            <>
              <ChartSettingsButton onSettingsChange={setChartSettings} />
              <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setVisibilityOpen(true)} data-testid="widget-visibility-btn" title="Widget Visibility">
                <Eye className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" className="h-9 w-9" onClick={openCommSettings} data-testid="commission-settings-btn" title="Commission Settings">
                <Settings className="h-4 w-4" />
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" className="h-9" onClick={handleRefresh} disabled={refreshing} data-testid="dash-refresh">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Personal Income */}
      {vis('dash_personal_income') && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-200 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => openKPI({
            title: isAE ? 'Pipeline Incentive (Personal)' : 'Pipeline Incentive (Company)',
            value: fmt$(income.pipeline_incentive || 0),
            formula: income.commission_settings?.pipeline_mode === 'percentage'
              ? `Pipeline Value × ${income.commission_settings?.pipeline_percentage}%`
              : `$${income.commission_settings?.pipeline_fixed_amount} × # of in-pipeline deals`,
            description: `Estimated incentive for ${isAE ? 'your' : 'the company\'s'} leads currently in opportunity pipeline stages (excluding Won/Lost). Calculated using the commission rule set in Commission Settings. Minimum contract value threshold: $${income.commission_settings?.pipeline_min_contract_value || 0}.`,
            inputs: [
              { label: 'Pipeline value (deals)', value: fmt$(kpi.pipeline_value || 0) },
              { label: 'Opportunity deals count', value: kpi.opportunities ?? 0 },
              { label: 'Commission mode', value: income.commission_settings?.pipeline_mode || 'percentage' },
            ],
            source: isAE ? 'Your assigned leads in opportunity stages' : 'All leads in opportunity stages',
          })}
          data-testid="dash-pipeline-incentive-card"
        >
          <CardContent className="p-5 flex items-center gap-4">
            <div className="p-3 bg-amber-200/50 rounded-xl"><Award className="h-7 w-7 text-amber-700" /></div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-amber-700 font-semibold uppercase tracking-wider">Pipeline Incentive</p>
              <p className="text-2xl font-bold text-amber-900">{fmt$(income.pipeline_incentive || 0)}</p>
              <p className="text-xs text-amber-600 truncate">Based on {income.commission_settings?.pipeline_mode === 'percentage' ? `${income.commission_settings?.pipeline_percentage}% of deal value` : `$${income.commission_settings?.pipeline_fixed_amount} per deal`}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 border-emerald-200">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="p-3 bg-emerald-200/50 rounded-xl"><DollarSign className="h-7 w-7 text-emerald-700" /></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-1">
                <p className="text-xs text-emerald-700 font-semibold uppercase tracking-wider">Incentive Won</p>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-6 w-6 text-emerald-700 hover:bg-emerald-200/50"
                    onClick={(e) => { e.stopPropagation(); shiftIncentiveMonth(-1); }}
                    data-testid="incentive-prev-month">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-[11px] font-semibold text-emerald-800 min-w-[90px] text-center" data-testid="incentive-month-label">
                    {income.incentive_month_label || 'This Month'}
                  </span>
                  <Button variant="ghost" size="icon" className="h-6 w-6 text-emerald-700 hover:bg-emerald-200/50 disabled:opacity-40"
                    onClick={(e) => { e.stopPropagation(); shiftIncentiveMonth(1); }}
                    disabled={isCurrentOrFutureMonth(incentiveMonth)}
                    data-testid="incentive-next-month">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="cursor-pointer" onClick={() => openKPI({
                title: `Incentive Won — ${income.incentive_month_label || 'This Month'}`,
                value: fmt$(income.win_incentive || 0),
                formula: income.commission_settings?.win_mode === 'percentage'
                  ? `Contract Value × ${income.commission_settings?.win_percentage}%`
                  : `$${income.commission_settings?.win_fixed_amount} × # of won contracts`,
                description: `${isAE ? 'Your personal' : 'Company-wide'} won incentive for ${income.incentive_month_label || 'this month'}, based on ${isAE ? 'contracts tied to your leads' : 'all contracts'} created within the selected month. Minimum contract value threshold: $${income.commission_settings?.win_min_contract_value || 0}. Use the arrows above to review earlier months.`,
                inputs: [
                  { label: 'Contracts signed', value: income.incentive_contracts_count ?? 0 },
                  { label: 'Contract value total', value: fmt$(income.incentive_contract_value || 0) },
                  { label: 'Commission mode', value: income.commission_settings?.win_mode || 'percentage' },
                ],
                source: isAE ? 'Your assigned leads with signed contracts' : 'All signed contracts company-wide',
              })}>
                <p className="text-2xl font-bold text-emerald-900">{fmt$(income.win_incentive || 0)}</p>
                <p className="text-xs text-emerald-600 truncate">
                  {income.incentive_contracts_count || 0} contract{(income.incentive_contracts_count || 0) === 1 ? '' : 's'} · {income.commission_settings?.win_mode === 'percentage' ? `${income.commission_settings?.win_percentage}% of contract value` : `$${income.commission_settings?.win_fixed_amount} per contract`}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      )}

      {/* 9 KPI Cards */}
      {vis('dash_kpi_cards') && (
      <div>
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Overall Performance (All Time)</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-3">
          <DashKPI title="Total Leads" value={kpi.total_leads ?? '-'} icon={Users} variant="blue"
            onClick={() => openKPI({
              title: 'Total Leads',
              value: String(kpi.total_leads ?? 0),
              formula: 'COUNT(leads) WHERE stage ∉ hidden staging stages',
              description: 'All-time lead count, excluding leads awaiting admin triage (landing_page_new, field_data_new). These hidden staging leads are surfaced separately in the Import Leads screen until an owner is assigned.',
              source: isAE ? 'Your assigned leads' : (repFilter !== 'all' ? `Leads assigned to selected rep` : 'All company leads'),
            })}
          />
          <DashKPI title={contactedLabel} value={kpi.contacted ?? '-'} icon={Phone} variant="blue"
            onClick={() => openKPI({
              title: contactedLabel,
              value: String(kpi.contacted ?? 0),
              formula: `COUNT(leads) WHERE stage order >= "${stageSem.contacted_stage_id || 'contacted'}"`,
              description: 'Leads that have progressed past the contact threshold (i.e., any stage from Contacted onwards in the configured pipeline order).',
              source: 'Stage Settings + Leads collection',
            })}
          />
          <DashKPI title={consultLabel} value={kpi.consultation_done ?? '-'} icon={Calendar} variant="purple"
            onClick={() => openKPI({
              title: consultLabel,
              value: String(kpi.consultation_done ?? 0),
              formula: `COUNT(leads) WHERE stage order >= "${stageSem.consult_done_stage_id || 'consultation_completed'}"`,
              description: 'Leads that have completed or passed the consultation step (including subsequent opportunity and terminal stages).',
              source: 'Stage Settings + Leads collection',
            })}
          />
          <DashKPI title={wonLabel} value={kpi.closed_won ?? '-'} icon={CheckSquare} variant="green"
            onClick={() => openKPI({
              title: wonLabel,
              value: String(kpi.closed_won ?? 0),
              formula: `COUNT(leads) WHERE stage ∈ {${(stageSem.won_stage_ids || []).join(', ')}}`,
              description: 'Leads that have reached a Won terminal stage. Won stages are detected dynamically via the `is_won` flag or the `closed_won` stage id fallback.',
              source: 'Stage Settings + Leads collection',
            })}
          />
          <DashKPI title="Opportunities" value={kpi.opportunities ?? '-'} icon={TrendingUp} variant="gold"
            onClick={() => openKPI({
              title: 'Opportunities',
              value: String(kpi.opportunities ?? 0),
              formula: 'COUNT(leads) WHERE stage ∈ opportunity-category AND not terminal',
              description: 'Leads currently inside the opportunity pipeline (excluding Won/Lost). Category is set per stage in Stage Settings.',
              source: 'Stage Settings + Leads collection',
            })}
          />
          <DashKPI title="Pipeline Value" value={fmt$(kpi.pipeline_value || 0)} icon={DollarSign} variant="gold"
            onClick={() => openKPI({
              title: 'Pipeline Value',
              value: fmt$(kpi.pipeline_value || 0),
              formula: 'SUM(leads.deal_value) WHERE stage ∈ opportunity-category AND not terminal',
              description: 'Sum of declared deal values across all open opportunity leads (excluding Won/Lost). Set when a lead is promoted to opportunity.',
              source: 'Leads.deal_value',
            })}
          />
          <DashKPI title="Cash Opportunity" value={fmt$(kpi.cash_opportunity || 0)} icon={DollarSign} variant="blue"
            onClick={() => openKPI({
              title: 'Cash Opportunity',
              value: fmt$(kpi.cash_opportunity || 0),
              formula: 'SUM(leads.estimated_cash_collection) WHERE stage ∈ opportunity-category AND not terminal',
              description: 'Sum of estimated cash to be collected across all open opportunity leads. Typically lower than Pipeline Value since deals often have financed portions.',
              source: 'Leads.estimated_cash_collection',
            })}
          />
          <DashKPI title="Cash Collected" value={fmt$(kpi.cash_collected || 0)} icon={DollarSign} variant="green"
            onClick={() => openKPI({
              title: 'Cash Collected',
              value: fmt$(kpi.cash_collected || 0),
              formula: 'SUM(payments.amount) WHERE contract.lead ∈ scope AND payment.voided=false',
              description: 'All-time cash collected from payments attached to contracts for leads in scope. Includes installments, booking money, down payment, future adjustments, and one-time payments.',
              source: 'Payments collection',
            })}
          />
          <DashKPI title="Monthly Install." value={fmt$(kpi.monthly_installment_size || 0)} icon={Briefcase} variant="purple"
            onClick={() => openKPI({
              title: 'Monthly Installment Size',
              value: fmt$(kpi.monthly_installment_size || 0),
              formula: 'SUM(contract.installment_amount) WHERE payment_status ∈ {Active, Overdue} AND contract_type ≠ one_time',
              description: 'Total active installment obligation coming in next payment cycle across all non-one-time contracts in scope. Useful for projecting upcoming cash inflow.',
              source: 'Financial contracts collection',
            })}
          />
        </div>
      </div>
      )}

      {/* Product Sales Charts */}
      {vis('dash_product_charts') && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base">Sales Count by Product</CardTitle>
            <Select value={String(chartYear)} onValueChange={v => setChartYear(Number(v))}>
              <SelectTrigger className="w-20 h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{[chartYear - 1, chartYear, chartYear + 1].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={parseInt(chartSettings.chartHeight) || 220}>
              <LineChart data={charts.product_sales_count || []} margin={{ left: 0, right: 10, top: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="land_plot" name="Land Plot" stroke={chartSettings.line1Color} strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="hotel_share" name="Hotel Share" stroke={chartSettings.line2Color} strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base">Sales Amount by Product</CardTitle>
            <Select value={String(chartYear)} onValueChange={v => setChartYear(Number(v))}>
              <SelectTrigger className="w-20 h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{[chartYear - 1, chartYear, chartYear + 1].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={parseInt(chartSettings.chartHeight) || 220}>
              <LineChart data={charts.product_sales_amount || []} margin={{ left: 0, right: 10, top: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={v => v >= 1000 ? `$${(v / 1000).toFixed(0)}K` : `$${v}`} />
                <Tooltip formatter={v => `$${Number(v).toLocaleString()}`} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="land_plot" name="Land Plot" stroke={chartSettings.line1Color} strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="hotel_share" name="Hotel Share" stroke={chartSettings.line2Color} strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Stage Funnel + Pie */}
      {(vis('dash_stage_funnel') || vis('dash_stage_distribution')) && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {vis('dash_stage_funnel') && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Stage Funnel</CardTitle></CardHeader>
          <CardContent>
            {stageDist.lead_stages && stageDist.opportunity_stages ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold text-blue-600 uppercase mb-2">Lead Stages</p>
                  {stageDist.lead_stages.map(s => {
                    const max = Math.max(...stageDist.lead_stages.map(x => x.count), 1);
                    return (
                      <div key={s.stage_id} className="mb-1.5">
                        <div className="flex justify-between text-[11px] text-slate-600 mb-0.5"><span>{s.label}</span><span className="font-medium">{s.count}</span></div>
                        <div className="h-2.5 bg-slate-100 rounded overflow-hidden">
                          <div className="h-full rounded" style={{ width: `${Math.max((s.count / max) * 100, 2)}%`, backgroundColor: chartSettings.funnelLeadColor }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div>
                  <p className="text-xs font-semibold text-purple-600 uppercase mb-2">Opportunity Stages</p>
                  {stageDist.opportunity_stages.map(s => {
                    const max = Math.max(...stageDist.opportunity_stages.map(x => x.count), 1);
                    const c = s.stage_id === 'closed_won' ? '#10b981' : s.stage_id === 'closed_lost' ? '#f87171' : chartSettings.funnelOppColor;
                    return (
                      <div key={s.stage_id} className="mb-1.5">
                        <div className="flex justify-between text-[11px] text-slate-600 mb-0.5"><span>{s.label}</span><span className="font-medium">{s.count}</span></div>
                        <div className="h-2.5 bg-slate-100 rounded overflow-hidden">
                          <div className="h-full rounded" style={{ width: `${Math.max((s.count / max) * 100, 2)}%`, backgroundColor: c }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : <p className="text-slate-400 text-center py-8">No data</p>}
          </CardContent>
        </Card>
        )}
        {vis('dash_stage_distribution') && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Stage Distribution</CardTitle></CardHeader>
          <CardContent>
            <MiniPie data={stageDist.all_stages || []} total={stageDist.total_leads || 0} />
          </CardContent>
        </Card>
        )}
      </div>
      )}

      {/* Today's Job + Achievements */}
      {(vis('dash_todays_job') || vis('dash_todays_achievements')) && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {vis('dash_todays_job') && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-base flex items-center gap-2"><Clock className="h-4 w-4 text-amber-500" /> Today's Job</CardTitle>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => shiftDate(setJobDate, jobDate, -1)} data-testid="job-prev-day" title="Previous day">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-[11px] font-semibold text-slate-700 min-w-[90px] text-center tabular-nums" data-testid="job-date-label">
                  {job.date_label || 'Today'}
                </span>
                <Button variant="ghost" size="icon" className="h-7 w-7 disabled:opacity-40" onClick={() => shiftDate(setJobDate, jobDate, 1)} disabled={isAtOrAfterToday(jobDate)} data-testid="job-next-day" title="Next day">
                  <ChevronRight className="h-4 w-4" />
                </Button>
                {!(job.is_today ?? true) && (
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setJobDate(todayISO())} data-testid="job-reset-today">Today</Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <JobBox label="New Leads" value={job.new_leads} color="blue" />
              <JobBox label="Delayed Contact" value={job.delayed_contact} color="red" />
              <JobBox label="Tasks Today" value={job.tasks_today} color="amber" />
              <JobBox label="Delayed Tasks" value={job.delayed_tasks} color="red" />
              <JobBox label="Appointments" value={job.appointments_today} color="green" />
              <JobBox label="Missed Appts" value={job.missed_appointments} color="red" />
            </div>
          </CardContent>
        </Card>
        )}
        {vis('dash_todays_achievements') && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-base flex items-center gap-2"><Target className="h-4 w-4 text-emerald-500" /> Today's Achievements</CardTitle>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => shiftDate(setAchDate, achDate, -1)} data-testid="ach-prev-day" title="Previous day">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-[11px] font-semibold text-slate-700 min-w-[90px] text-center tabular-nums" data-testid="ach-date-label">
                  {ach.date_label || 'Today'}
                </span>
                <Button variant="ghost" size="icon" className="h-7 w-7 disabled:opacity-40" onClick={() => shiftDate(setAchDate, achDate, 1)} disabled={isAtOrAfterToday(achDate)} data-testid="ach-next-day" title="Next day">
                  <ChevronRight className="h-4 w-4" />
                </Button>
                {!(ach.is_today ?? true) && (
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setAchDate(todayISO())} data-testid="ach-reset-today">Today</Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <AchBox label="Calls Made" value={ach.calls_made} icon={Phone} />
              <AchBox label="Texts Sent" value={ach.texts_sent} icon={MessageSquare} />
              <AchBox label="New Appts" value={ach.new_appointments} icon={Calendar} />
              <AchBox label="Consult Done" value={ach.consultation_completed} icon={CheckSquare} />
              <AchBox label="Opp Created" value={ach.opportunity_created} icon={TrendingUp} />
              <AchBox label="Pipeline Created" value={fmt$(ach.pipeline_value_created || 0)} icon={DollarSign} />
              <AchBox label="Cash Opp Created" value={fmt$(ach.cash_opportunity_created || 0)} icon={DollarSign} />
            </div>
          </CardContent>
        </Card>
        )}
      </div>
      )}

      {/* Opportunities Table */}
      {vis('dash_opportunities_table') && (
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Opportunities ({(d.opportunities_table || []).length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          {(d.opportunities_table || []).length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead>Deal Value</TableHead>
                    <TableHead>Cash Expect.</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Latest Note</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(d.opportunities_table || []).map(opp => (
                    <TableRow key={opp.lead_id}>
                      <TableCell className="font-medium text-sm">{opp.name}</TableCell>
                      <TableCell className="text-xs text-slate-600">{opp.phone || opp.email || '-'}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{opp.stage_label}</Badge></TableCell>
                      <TableCell className="text-sm font-medium">{fmt$(opp.deal_value)}</TableCell>
                      <TableCell className="text-sm">{fmt$(opp.cash_expectation)}</TableCell>
                      <TableCell className="text-xs">{opp.product_interest || '-'}</TableCell>
                      <TableCell className="text-xs text-slate-500 max-w-[180px] truncate">{opp.latest_note || '-'}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setActivityModal({ open: true, leadId: opp.lead_id })} data-testid={`opp-view-${opp.lead_id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : <p className="text-slate-400 text-center py-8">No active opportunities</p>}
        </CardContent>
      </Card>
      )}

      {/* Commission Settings Dialog */}
      <Dialog open={commOpen} onOpenChange={setCommOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Settings className="h-5 w-5" /> Commission Settings</DialogTitle></DialogHeader>
          {commSettings && (
            <div className="space-y-5 py-2">
              {/* Pipeline Incentive */}
              <div className="space-y-3 p-4 bg-amber-50 rounded-lg">
                <h4 className="font-semibold text-sm text-amber-800">Pipeline Incentive</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Mode</Label>
                    <Select value={commSettings.pipeline_mode} onValueChange={v => setCommSettings(p => ({ ...p, pipeline_mode: v }))}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">% of Deal Value</SelectItem>
                        <SelectItem value="fixed">Fixed Amount per Deal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {commSettings.pipeline_mode === 'percentage' ? (
                    <div><Label className="text-xs">Percentage (%)</Label>
                      <Input type="number" className="h-8 text-xs" value={commSettings.pipeline_percentage} onChange={e => setCommSettings(p => ({ ...p, pipeline_percentage: parseFloat(e.target.value) || 0 }))} /></div>
                  ) : (
                    <div><Label className="text-xs">Fixed Amount ($)</Label>
                      <Input type="number" className="h-8 text-xs" value={commSettings.pipeline_fixed_amount} onChange={e => setCommSettings(p => ({ ...p, pipeline_fixed_amount: parseFloat(e.target.value) || 0 }))} /></div>
                  )}
                </div>
                <div><Label className="text-xs">Min Contract Value ($)</Label>
                  <Input type="number" className="h-8 text-xs" value={commSettings.pipeline_min_contract_value} onChange={e => setCommSettings(p => ({ ...p, pipeline_min_contract_value: parseFloat(e.target.value) || 0 }))} /></div>
              </div>

              {/* Win Incentive */}
              <div className="space-y-3 p-4 bg-emerald-50 rounded-lg">
                <h4 className="font-semibold text-sm text-emerald-800">Win Incentive</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Mode</Label>
                    <Select value={commSettings.win_mode} onValueChange={v => setCommSettings(p => ({ ...p, win_mode: v }))}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">% of Contract Value</SelectItem>
                        <SelectItem value="fixed">Fixed Amount per Contract</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {commSettings.win_mode === 'percentage' ? (
                    <div><Label className="text-xs">Percentage (%)</Label>
                      <Input type="number" className="h-8 text-xs" value={commSettings.win_percentage} onChange={e => setCommSettings(p => ({ ...p, win_percentage: parseFloat(e.target.value) || 0 }))} /></div>
                  ) : (
                    <div><Label className="text-xs">Fixed Amount ($)</Label>
                      <Input type="number" className="h-8 text-xs" value={commSettings.win_fixed_amount} onChange={e => setCommSettings(p => ({ ...p, win_fixed_amount: parseFloat(e.target.value) || 0 }))} /></div>
                  )}
                </div>
                <div><Label className="text-xs">Min Contract Value ($)</Label>
                  <Input type="number" className="h-8 text-xs" value={commSettings.win_min_contract_value} onChange={e => setCommSettings(p => ({ ...p, win_min_contract_value: parseFloat(e.target.value) || 0 }))} /></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCommOpen(false)}>Cancel</Button>
            <Button onClick={saveCommSettings} disabled={commSaving}>{commSaving ? 'Saving...' : 'Save Settings'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activity History Modal */}
      {activityModal.open && (
        <ActivityHistoryModal
          leadId={activityModal.leadId}
          isOpen={activityModal.open}
          onClose={() => setActivityModal({ open: false, leadId: null })}
        />
      )}

      {/* Dashboard Visibility Panel */}
      <DashboardVisibilityPanel open={visibilityOpen} onOpenChange={setVisibilityOpen} />
      {/* KPI definition modal */}
      {kpiModal}
    </div>
  );
}

// Small helper components
function JobBox({ label, value, color }) {
  const bg = color === 'red' ? 'bg-red-50 border-red-100' : color === 'blue' ? 'bg-blue-50 border-blue-100' : color === 'amber' ? 'bg-amber-50 border-amber-100' : 'bg-emerald-50 border-emerald-100';
  const txt = color === 'red' ? 'text-red-700' : color === 'blue' ? 'text-blue-700' : color === 'amber' ? 'text-amber-700' : 'text-emerald-700';
  return (
    <div className={`p-3 rounded-lg border ${bg}`} data-testid={`job-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <p className={`text-xl font-bold ${txt}`}>{value ?? 0}</p>
      <p className="text-xs text-slate-600">{label}</p>
    </div>
  );
}

function AchBox({ label, value, icon: Icon }) {
  return (
    <div className="flex items-center gap-2 p-2.5 bg-slate-50 rounded-lg" data-testid={`ach-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <Icon className="h-4 w-4 text-slate-400 flex-shrink-0" />
      <div className="min-w-0">
        <p className="text-sm font-bold text-slate-900">{value ?? 0}</p>
        <p className="text-[10px] text-slate-500 truncate">{label}</p>
      </div>
    </div>
  );
}
